import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import type { LayoutMode } from '../../../../shared/constants/layoutBreakpoints';
import {
  getOnboardingTourSteps,
  waitForOnboardingTargets,
  type OnboardingTourStepId,
  type OnboardingTourStepConfig,
} from './onboardingSteps';
import { useOnboardingStore } from './useOnboardingStore';

import './onboardingTour.module.css';

type TourTranslator = (key: string, params?: Record<string, string | number>) => string;

type RunOnboardingTourParams = {
  t: TourTranslator;
  layoutMode: LayoutMode;
  onComplete?: () => void;
};

const getStepCopy = (t: TourTranslator, stepId: OnboardingTourStepId) => ({
  title: t(`onboarding.tour.${stepId}.title`),
  description: t(`onboarding.tour.${stepId}.description`),
});

const buildSteps = (
  stepConfigs: OnboardingTourStepConfig[],
  t: TourTranslator,
  isDesktop: boolean,
): DriveStep[] => stepConfigs.map((step) => {
  const copy = getStepCopy(t, step.id);
  return {
    element: step.selector,
    popover: {
      title: copy.title,
      description: copy.description,
      side: isDesktop ? 'bottom' : 'top',
      align: 'center' as const,
    },
  };
});

export const runOnboardingTour = async ({
  t,
  layoutMode,
  onComplete,
}: RunOnboardingTourParams): Promise<void> => {
  const stepConfigs = getOnboardingTourSteps(layoutMode);
  const isDesktop = layoutMode === 'desktop';
  const firstStep = stepConfigs[0];

  if (firstStep) {
    await waitForOnboardingTargets([firstStep.selector]);
  }

  let driverObj: Driver | null = null;

  driverObj = driver({
    showProgress: true,
    animate: true,
    smoothScroll: true,
    allowClose: true,
    overlayOpacity: 0.55,
    stagePadding: 8,
    stageRadius: 8,
    nextBtnText: t('onboarding.tour.next'),
    prevBtnText: t('onboarding.tour.prev'),
    doneBtnText: t('onboarding.tour.done'),
    progressText: t('onboarding.tour.progress'),
    popoverClass: 'werstack-onboarding-popover',
    onHighlightStarted: (_element, _step, { state }) => {
      if (layoutMode !== 'tablet') {
        return;
      }

      const activeStep = stepConfigs[state.activeIndex];
      if (!activeStep) {
        return;
      }

      useOnboardingStore.getState().setSidebarOverlayOpen(false);
      driverObj?.refresh();
    },
    onDestroyed: () => {
      if (layoutMode === 'tablet') {
        useOnboardingStore.getState().setSidebarOverlayOpen(false);
      }
      onComplete?.();
    },
  });

  const steps = buildSteps(stepConfigs, t, isDesktop);

  if (!steps.length) {
    return;
  }

  driverObj.setSteps(steps);
  driverObj.drive();
};
